import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { saveDraftPages } from '../service';
import { toSnapshotPages } from '../components/BulkUpload/utils/draftPageSnapshot';
import TopBannerBox from '../components2/TopBannerBox';
import GeneralPopup from '../components/GeneralPopup';

/**
 * Autosaves the wizard's page list to PUT /assignments/:id/draftPages.
 *
 * Submissions do not exist until the Organise commit, so until then nothing on
 * the server references the uploaded files and a refresh loses the page list.
 * This is what closes that window.
 *
 * One hook owns the version, the debounce and the conflict state. Spreading PUTs
 * across the drag-drop and organise steps would give two components independent
 * opinions about the version number, and the loser would overwrite the winner.
 */

// Coalesce multi-slot drags. Navigation must stay guarded until acknowledgement;
// no debounce duration can guarantee that a teacher's latest change is saved.
const DEBOUNCE_MS = 800;

// A failed write is retried on a rising delay rather than dropped. The effect
// only re-arms on the next change, so without this a teacher who stopped editing
// after a 500 or a dropped connection would never save the page list at all.
// Bounded, because past this point the banner's Retry is the honest answer.
const RETRY_DELAYS_MS = [1000, 4000, 15000];

// Defaulting belongs to the service layer, which applies it in both directions —
// doing it here too ran the same rule three times on one write.
const snapshotOf = (pages, studentAssignments, students = []) => ({
  pages: toSnapshotPages(pages),
  studentAssignments,
  ...(students.some(student => /^(temp_|local-)/.test(student.id)) ? {
    studentNames: Object.fromEntries(students.filter(student => /^(temp_|local-)/.test(student.id)).map(student => [student.id, student.name])),
  } : {}),
});

// Compared as JSON rather than by reference: the wizard rebuilds these arrays on
// every render, so reference equality would save on every keystroke elsewhere in
// the flow, and a deep-equality helper would be the same comparison written twice.
const fingerprintOf = (snapshot) => JSON.stringify(snapshot);

export const useDraftPageSync = ({
  enabled = true,
  assignmentId,
  pages,
  studentAssignments,
  students,
  initialVersion = 0,
  isCommitted = false,
  onConflict,
  onError,
} = {}) => {
  const [hasConflict, setHasConflict] = useState(false);
  // Drives the persistent "not saved" bar. A boolean rather than the error
  // itself: what the teacher can act on is that the draft is behind, not which
  // status code it was.
  const [hasSaveError, setHasSaveError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const versionRef = useRef(initialVersion);
  const isSuppressed = !enabled || !assignmentId || isCommitted || hasConflict;

  // Computed once per render and shared with the effect. The wizard re-renders on
  // a timer during submit, and building this eagerly meant stringifying every
  // page — and DOM-parsing every manual one — on renders that could not save.
  const current = useMemo(
    () => (isSuppressed ? null : snapshotOf(pages, studentAssignments, students)),
    [isSuppressed, pages, studentAssignments, students],
  );
  const fingerprint = useMemo(
    () => (current ? fingerprintOf(current) : null),
    [current],
  );

  // Seeded lazily with what the wizard was handed, so a resumed draft does not
  // immediately save back the snapshot it was just built from — and so the seed
  // is computed once rather than on every render.
  // Seeded on the first render that could actually save, so a hook that mounts
  // suppressed (no assignment yet) still adopts what is on screen when it wakes
  // rather than treating it as an unsaved change.
  const [savedFingerprint, setSavedFingerprint] = useState(undefined);
  if (savedFingerprint === undefined && fingerprint !== null) {
    setSavedFingerprint(fingerprint);
  }
  const pendingRef = useRef(null);
  const inFlightRef = useRef(null);
  const conflictRef = useRef(null);
  const timerRef = useRef(null);
  const attemptsRef = useRef(0);
  // Lets a retry re-enter flush without flush having to be declared first.
  const flushRef = useRef(null);
  // Keeps put/flush referentially stable: without it an inline onConflict would
  // change flush's identity every render, and the effect's cleanup would reset
  // the debounce before it ever fired.
  const callbacksRef = useRef({ onConflict, onError });
  callbacksRef.current = { onConflict, onError };

  const isConflict = (error) => error?.code === 'DRAFT_PAGES_CONFLICT';

  const retryDelay = () =>
    RETRY_DELAYS_MS[Math.min(attemptsRef.current, RETRY_DELAYS_MS.length - 1)];

  const scheduleRetry = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => flushRef.current?.().catch(() => {}), retryDelay());
    attemptsRef.current += 1;
  }, []);

  // The write stays pending and armed on a timer. Clearing it here is what left
  // a page list permanently unsaved once the teacher stopped editing.
  const retryLater = useCallback(
    (pending) => {
      pendingRef.current ||= pending;
      setHasSaveError(true);
      if (attemptsRef.current < RETRY_DELAYS_MS.length) scheduleRetry();
    },
    [scheduleRetry],
  );

  const handleConflict = useCallback((error) => {
    // Never retried: a blind retry is exactly how one tab overwrites the other.
    setHasConflict(true);
    conflictRef.current = error;
    callbacksRef.current.onConflict?.(error?.body?.version ?? null);
  }, []);

  const handleFailure = useCallback(
    (error, pending) => {
      if (isConflict(error)) return handleConflict(error);
      callbacksRef.current.onError?.(error);
      return retryLater(pending);
    },
    [handleConflict, retryLater],
  );

  const markSaved = useCallback((version, fingerprint) => {
    versionRef.current = version;
    setSavedFingerprint(fingerprint);
    attemptsRef.current = 0;
    setHasSaveError(false);
  }, []);

  const put = useCallback(
    async (snapshot, fingerprint) => {
      try {
        const result = await saveDraftPages(assignmentId, {
          ...snapshot,
          version: versionRef.current,
        });
        markSaved(result?.version ?? versionRef.current + 1, fingerprint);
      } catch (error) {
        handleFailure(error, { snapshot, fingerprint });
        throw error;
      }
    },
    [assignmentId, handleFailure, markSaved],
  );

  const flush = useCallback(async () => {
    clearTimeout(timerRef.current);
    if (conflictRef.current) throw conflictRef.current;
    // A commit must await both the already-running PUT and any edits queued
    // behind it. Each PUT sees the version returned by its predecessor.
    while (inFlightRef.current || pendingRef.current) {
      if (inFlightRef.current) {
        await inFlightRef.current.promise;
        continue;
      }
      const pending = pendingRef.current;
      pendingRef.current = null;
      setIsSaving(true);
      const saving = put(pending.snapshot, pending.fingerprint);
      inFlightRef.current = { promise: saving, fingerprint: pending.fingerprint };
      try {
        await saving;
      } finally {
        if (inFlightRef.current?.promise === saving) {
          inFlightRef.current = null;
          setIsSaving(false);
        }
      }
    }
  }, [put]);
  flushRef.current = flush;

  // The automatic backoff is bounded, so once it is spent this is the only way
  // the pending write goes out without the teacher touching the page list again.
  const retry = useCallback(() => {
    attemptsRef.current = 0;
    return flush().catch(() => {});
  }, [flush]);

  // Only a changed snapshot should re-arm the debounce. Fresh roster objects
  // and save-state renders must leave the bounded retry timer alone. Read the
  // latest acknowledgement/error state when an actual snapshot change occurs.
  const snapshotStateRef = useRef(null);
  snapshotStateRef.current = { current, savedFingerprint, hasSaveError };
  useEffect(() => {
    const { current, savedFingerprint, hasSaveError } = snapshotStateRef.current;
    // Compare with what the running PUT will save, if any. Reverting to the old
    // server snapshot still needs a write behind it. Reverting before a PUT
    // starts, or back to its exact payload, cancels an obsolete queued snapshot.
    // After a failed response the server outcome may be unknown. Even a revert
    // needs acknowledgement (or a version conflict), rather than dropping retry.
    const targetFingerprint = inFlightRef.current?.fingerprint ??
      (hasSaveError ? null : savedFingerprint);
    pendingRef.current = current && fingerprint !== targetFingerprint
      ? { snapshot: current, fingerprint }
      : null;
    clearTimeout(timerRef.current);
    if (!pendingRef.current) return undefined;
    timerRef.current = setTimeout(() => flush().catch(() => {}), DEBOUNCE_MS);
    return () => clearTimeout(timerRef.current);
  }, [fingerprint, flush]);

  // A retry timer outlives the effect above, which re-arms only on a change — so
  // without this one a backoff could fire a PUT after the wizard had closed.
  useEffect(() => () => clearTimeout(timerRef.current), []);

  // No `version`: it lives in a ref, so returning it would hand callers a value
  // that never triggers a render and is stale the moment a save lands.
  // Derive dirty work during render, before the debounce effect queues it, and
  // render again on acknowledgement so navigation is released without an edit.
  const hasUnsavedChanges = enabled && !isCommitted && Boolean(
    hasConflict || hasSaveError || isSaving ||
    (fingerprint !== null && fingerprint !== savedFingerprint)
  );
  return { flush, retry, hasConflict, hasSaveError, hasUnsavedChanges,
    hasPending: () => Boolean(pendingRef.current || inFlightRef.current),
  };
};

const reloadPage = () => window.location.reload();

// A bar rather than a toast: a toast disappears while the page list is still
// unsaved, which is the one fact it exists to keep on screen.
const UnsavedBanner = ({ onRetry }) => (
  <TopBannerBox
    bannerMessage="Changes not saved. Keep this tab open — we're still trying."
    confirmButtonLabel="Retry"
    onConfirm={onRetry}
  />
);

// Blocking: the conflict stops every future write for the life of the wizard, so
// anything less lets the teacher keep organising into a void. No hidePopup —
// both ways out are on the dialog, and dismissing it would only hide the loss.
const ConflictPopup = ({ onReload, onDiscard }) => (
  <GeneralPopup
    title="This draft changed in another tab"
    textContent="Your page order and student routing are no longer being saved. Reload to pick up the other tab's version, or leave and discard what you have changed here."
    buttonText="Reload"
    confirmButtonAction={onReload}
    closeBtnText="Discard my changes"
    cancelPopup={onDiscard || onReload}
    hidePopup={() => {}}
  />
);

/**
 * The hook's two failure states, rendered. It lives beside the hook rather than
 * in the wizard so the states cannot drift from what the hook exposes, and so
 * the wizard's own change is a single element.
 */
export const DraftPageSyncStatus = ({
  hasConflict,
  hasSaveError,
  onRetry,
  onDiscard,
  onReload = reloadPage,
}) => {
  // Conflict first: the bar's retry can only fail while one is outstanding.
  if (hasConflict)
    return <ConflictPopup onReload={onReload} onDiscard={onDiscard} />;
  return hasSaveError ? <UnsavedBanner onRetry={onRetry} /> : null;
};
